import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaPlus, FaSyncAlt, FaCopy, FaDownload, FaCheck } from 'react-icons/fa';
import TrialLock from '../components/TrialLock';
import InfoBanner from '../components/InfoBanner';
import Select from 'react-select';
import axiosInstance from "../utility/axiosInstance";
import { handleAxiosError } from "../helpers/handleAxiosError";
import { useToast } from "../components/ToastContext";
import Modal from "../components/Modal";
import DeleteKeypairModal from "../components/DeleteKeypairModal";
import type { AppDispatch, RootState } from "../store/store";
import { fetchKeypairs } from "../store/slices/keypairsSlice";
import type { Keypair } from "../store/slices/keypairsSlice";

const formatKeyCreatedAt = (value?: string) => {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(date);
};

const getPublicKeyType = (keypair: Keypair) => keypair.public_key?.trim().split(/\s+/)[0] || null;

const copyTextToClipboard = async (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
        if (!document.execCommand('copy')) {
            throw new Error('Copy command was unavailable');
        }
    } finally {
        document.body.removeChild(textArea);
    }
};

const KeyPairsPage: React.FC = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [newKeypairName, setNewKeypairName] = useState<string>('');
    const [newPublicKey, setNewPublicKey] = useState<string>('');
    const [showCreateKeypair, setShowCreateKeypair] = useState(false);
    const [keypairToDelete, setKeypairToDelete] = useState<Keypair | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [createMode, setCreateMode] = useState<'upload' | 'generate'>('upload');
    const [generatedPrivateKey, setGeneratedPrivateKey] = useState<string | null>(null);
    const [generatedKeypairName, setGeneratedKeypairName] = useState<string>('');
    const [hasAcknowledgedPrivateKey, setHasAcknowledgedPrivateKey] = useState(false);
    const [copiedFingerprintName, setCopiedFingerprintName] = useState<string | null>(null);

    const dispatch = useDispatch<AppDispatch>();
    const keyPairs = useSelector((state: RootState) => state.keypairs);

    const sortedKeypairs = keyPairs.data.slice().sort((a, b) => {
        if (!a.created_at && !b.created_at) return 0;
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });


    const handleCreateKeypair = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const requestBody = {
                name: newKeypairName,
                publicKey: newPublicKey,
            };

            const response = await axiosInstance.post('/v1/keypairs', requestBody);

            if (response.status === 201) {
                showToast('Keypair created successfully!', 'success');
                dispatch(fetchKeypairs());
                setNewKeypairName('');
                setNewPublicKey('');
                setShowCreateKeypair(false);
            } else {
                throw new Error('Failed to create keypair');
            }
        } catch (err) {
            console.error('Error creating keypair:', err);
            handleAxiosError(err);
            showToast('Error creating keypair!', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateKeypair = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.post('/v1/keypairs/generate', {
                name: newKeypairName,
            });

            if (response.status === 201) {
                showToast('Keypair generated successfully!', 'success');
                dispatch(fetchKeypairs());
                setGeneratedPrivateKey(response.data.private_key ?? null);
                setGeneratedKeypairName(newKeypairName);
                setHasAcknowledgedPrivateKey(false);
                setNewKeypairName('');
                setShowCreateKeypair(false);
            } else {
                throw new Error('Failed to generate keypair');
            }
        } catch (err) {
            console.error('Error generating keypair:', err);
            handleAxiosError(err);
            showToast('Error generating keypair!', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleClosePrivateKeyModal = () => {
        setGeneratedPrivateKey(null);
        setGeneratedKeypairName('');
        setHasAcknowledgedPrivateKey(false);
    };

    const handleCopyPrivateKey = async () => {
        if (!generatedPrivateKey) return false;
        try {
            await navigator.clipboard.writeText(generatedPrivateKey);
            showToast('Private key copied!', 'success');
            return true;
        } catch {
            showToast('Failed to copy. Select the key and copy it manually.', 'error');
            return false;
        }
    };

    const handleDownloadPrivateKey = () => {
        if (!generatedPrivateKey) return;
        const blob = new Blob([generatedPrivateKey], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${generatedKeypairName}.pem`;
        a.click();
        URL.revokeObjectURL(url);
        setHasAcknowledgedPrivateKey(true);
    };

    const handleKeypairAction = (action: string, keypair: Keypair) => {
        if (action === 'delete') {
            setKeypairToDelete(keypair);
        }
    };

    const handleCopyFingerprint = async (keypair: Keypair) => {
        try {
            await copyTextToClipboard(keypair.fingerprint);
            setCopiedFingerprintName(keypair.name);
            showToast(`Fingerprint for ${keypair.name} copied`, 'success');
        } catch {
            showToast('Could not copy the fingerprint. Select it and copy it manually.', 'error');
        }
    };

    const handleConfirmDelete = async () => {
        if (!keypairToDelete) return;
        setIsDeleting(true);
        try {
            const response = await axiosInstance.delete(`/v1/keypairs/${keypairToDelete.name}`);
            if (response.status === 204) {
                showToast(`Keypair "${keypairToDelete.name}" deleted successfully`, 'success');
                dispatch(fetchKeypairs());
            } else {
                throw new Error(`Failed to delete keypair "${keypairToDelete.name}"`);
            }
        } catch (err) {
            console.error(err);
            handleAxiosError(err);
            showToast(`Failed to delete keypair "${keypairToDelete.name}"`, 'error');
        } finally {
            setIsDeleting(false);
            setKeypairToDelete(null);
        }
    };

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <h1 className="page-title">SSH keys</h1>
                <p className="page-subtitle">Add a public key you already control or generate a new keypair before launching a virtual machine.</p>
            </div>

            {/* Dismissible, like the other pages' notes (Milos 2026-10-26); a closed note stays closed */}
            <InfoBanner id="ssh-keys-public-only">
                <span><strong>Public keys only.</strong> Pick one when you launch a VM. Never paste a private key here.</span>
            </InfoBanner>

            <p className="visually-hidden" role="status" aria-live="polite">
                {copiedFingerprintName ? `Fingerprint for ${copiedFingerprintName} copied.` : ''}
            </p>

            {sortedKeypairs.length > 0 && (
                <InfoBanner id="ssh-keys-before-deleting" iconColor="var(--status-warning)" style={{ marginBottom: '1rem' }}>
                    <span><strong>Before deleting a key:</strong> new VMs can no longer use it, so make sure the VMs launched with it can still be reached another way.</span>
                </InfoBanner>
            )}

            
            <div className="desktop-view">
                    <div className="table-container">
                        <table className="keypairs-table">
                            <thead>
                            <tr>
                                <th>SSH key</th>
                                <th>Fingerprint</th>
                                <th>Details</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {keyPairs.status === 'loading' ? (
                                <tr>
                                    <td colSpan={4} className="loading-state">
                                        <FaSyncAlt className="spin"/> Loading keypairs...
                                    </td>
                                </tr>
                            ) : keyPairs.status === 'failed' ? (
                                <tr>
                                    <td colSpan={4} className="error-state" role="alert">
                                        Error: {keyPairs.error || 'Failed to load keypairs'}
                                    </td>
                                </tr>
                            ) :
                            sortedKeypairs.length > 0 ? (
                                sortedKeypairs.map((kp) => {
                                    const keyType = getPublicKeyType(kp);
                                    const createdAt = formatKeyCreatedAt(kp.created_at);

                                    return (
                                        <tr key={kp.fingerprint}>
                                            <td className="keypair-name">{kp.name}</td>
                                            <td className="keypair-fingerprint">
                                                <div className="keypair-fingerprint-cell">
                                                    <code>{kp.fingerprint}</code>
                                                    <button
                                                        type="button"
                                                        className="keypair-copy-button"
                                                        onClick={() => handleCopyFingerprint(kp)}
                                                        aria-label={`Copy fingerprint for ${kp.name}`}
                                                        title={`Copy fingerprint for ${kp.name}`}
                                                    >
                                                        {copiedFingerprintName === kp.name ? <FaCheck aria-hidden="true" /> : <FaCopy aria-hidden="true" />}
                                                        <span>{copiedFingerprintName === kp.name ? 'Copied' : 'Copy'}</span>
                                                    </button>
                                                </div>
                                            </td>
                                            <td>
                                                {keyType || createdAt ? (
                                                    <div className="keypair-details">
                                                        {keyType && <span>{keyType}</span>}
                                                        {createdAt && <span>Created {createdAt}</span>}
                                                    </div>
                                                ) : (
                                                    <span className="keypair-detail-unavailable">No additional details available</span>
                                                )}
                                            </td>
                                            <td>
                                            <div className="action-dropdown">
                                                <Select
                                                    options={[
                                                        {value: 'delete', label: 'Delete SSH key'}
                                                    ]}
                                                    onChange={(option) => {
                                                        if (option && option.value === 'delete') {
                                                            handleKeypairAction('delete', kp);
                                                        }
                                                    }}
                                                    placeholder="Actions"
                                                    isSearchable={false}
                                                    menuPortalTarget={document.body}
                                                    styles={getActionSelectStyles()}
                                                    isDisabled={loading}
                                                    aria-label={`Actions for SSH key ${kp.name}`}
                                                />
                                            </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={4} className="empty-state">
                                        <div className="resource-empty-state">
                                            <strong>No SSH keys yet</strong>
                                            <span>Import an existing public key or generate a new keypair before launching a virtual machine.</span>
                                            <button type="button" onClick={() => setShowCreateKeypair(true)}>Add SSH key</button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
            </div>

            
            <div className="mobile-view">
                {keyPairs.status === 'loading' ? (
                    <div className="loading-state">
                        <FaSyncAlt className="spin" />
                        <p>Loading keypairs...</p>
                    </div>
                ) : keyPairs.status === 'failed' ? (
                    <div className="error-state">
                        <p>Error: {keyPairs.error || 'Failed to load keypairs'}</p>
                    </div>
                ) : (
                    <div className="cards-container">
                        {sortedKeypairs.length > 0 ? (
                            sortedKeypairs.map((kp) => {
                                const keyType = getPublicKeyType(kp);
                                const createdAt = formatKeyCreatedAt(kp.created_at);

                                return (
                                    <div key={kp.fingerprint} className="keypair-card">
                                        <div className="card-header">
                                            <h3 className="card-title">{kp.name}</h3>
                                            <div className="action-dropdown" style={{width: '100%'}}>
                                                <Select
                                                    options={[
                                                        {value: 'delete', label: 'Delete SSH key'}
                                                    ]}
                                                    onChange={(option) => {
                                                        if (option && option.value === 'delete') {
                                                            handleKeypairAction('delete', kp);
                                                        }
                                                    }}
                                                    placeholder="Actions"
                                                    isSearchable={false}
                                                    menuPortalTarget={document.body}
                                                    styles={getActionSelectStyles()}
                                                    isDisabled={loading}
                                                    aria-label={`Actions for SSH key ${kp.name}`}
                                                />
                                            </div>
                                        </div>
                                        <div className="card-content">
                                            <div className="fingerprint">
                                                <span className="label">Fingerprint</span>
                                                <div className="keypair-fingerprint-cell">
                                                    <code>{kp.fingerprint}</code>
                                                    <button
                                                        type="button"
                                                        className="keypair-copy-button"
                                                        onClick={() => handleCopyFingerprint(kp)}
                                                        aria-label={`Copy fingerprint for ${kp.name}`}
                                                    >
                                                        {copiedFingerprintName === kp.name ? <FaCheck aria-hidden="true" /> : <FaCopy aria-hidden="true" />}
                                                        <span>{copiedFingerprintName === kp.name ? 'Copied' : 'Copy'}</span>
                                                    </button>
                                                </div>
                                            </div>
                                            {(keyType || createdAt) && (
                                                <div className="keypair-details keypair-details--mobile">
                                                    {keyType && <span>{keyType}</span>}
                                                    {createdAt && <span>Created {createdAt}</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="empty-state">
                                <div className="resource-empty-state">
                                    <strong>No SSH keys yet</strong>
                                    <span>Import an existing public key or generate a new keypair before launching a virtual machine.</span>
                                    <button type="button" onClick={() => setShowCreateKeypair(true)}>Add SSH key</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            
            <TrialLock locked={false}>
            <button
                className="create-button"
                onClick={() => setShowCreateKeypair(true)}
                title="Add SSH key"
            >
                <FaPlus />
                <span>Add SSH key</span>
            </button>
            </TrialLock>

            
            <Modal
                isOpen={showCreateKeypair}
                onClose={() => {
                    setShowCreateKeypair(false);
                    setNewKeypairName('');
                    setNewPublicKey('');
                    setCreateMode('upload');
                }}
                title="Add SSH key"
            >
                <p className="keypair-creation-intro">Choose whether to import a public key you already manage or have us generate a new keypair for you.</p>
                <div className="keypair-mode-toggle">
                    <button
                        type="button"
                        className={`mode-tab${createMode === 'upload' ? ' active' : ''}`}
                        onClick={() => setCreateMode('upload')}
                        aria-pressed={createMode === 'upload'}
                    >
                        Import public key
                    </button>
                    <button
                        type="button"
                        className={`mode-tab${createMode === 'generate' ? ' active' : ''}`}
                        onClick={() => setCreateMode('generate')}
                        aria-pressed={createMode === 'generate'}
                    >
                        Generate Keypair
                    </button>
                </div>

                {createMode === 'upload' ? (
                    <form onSubmit={handleCreateKeypair} className="create-form">
                        <p className="keypair-mode-description">Use this if you already have the private key on your device. Paste only its matching public key below.</p>
                        <div className="form-group">
                            <label htmlFor="ssh-key-name-upload">SSH key name</label>
                            <input
                                id="ssh-key-name-upload"
                                type="text"
                                value={newKeypairName}
                                onChange={(e) => setNewKeypairName(e.target.value)}
                                required
                                placeholder="e.g., my-keypair-01"
                                autoFocus
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="ssh-public-key">Public key</label>
                            <textarea
                                id="ssh-public-key"
                                value={newPublicKey}
                                onChange={(e) => setNewPublicKey(e.target.value)}
                                required
                                placeholder="ssh-rsa AAAAB3NzaC1yc2E..."
                                rows={4}
                            />
                            <span className="field-help">Public keys usually begin with <code>ssh-ed25519</code>, <code>ssh-rsa</code>, or <code>ecdsa-sha2-</code>. Never paste a private key.</span>
                        </div>
                        <p className="help-text">
                            Not sure how to generate a key?{' '}
                            <a
                                href="https://docs.redu.cloud/Guides/keypairs-guide"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Read our guide
                            </a>
                        </p>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="cancel-button"
                                onClick={() => {
                                    setShowCreateKeypair(false);
                                    setNewKeypairName('');
                                    setNewPublicKey('');
                                    setCreateMode('upload');
                                }}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button disabled={loading} className="submit-button" type="submit">
                                {loading ? 'Importing...' : 'Import public key'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleGenerateKeypair} className="create-form">
                        <p className="keypair-mode-description">Use this if you need a new keypair. Download or copy the private key immediately after generation because it is shown only once.</p>
                        <div className="form-group">
                            <label htmlFor="ssh-key-name-generate">SSH key name</label>
                            <input
                                id="ssh-key-name-generate"
                                type="text"
                                value={newKeypairName}
                                onChange={(e) => setNewKeypairName(e.target.value)}
                                required
                                placeholder="e.g., my-keypair-01"
                                autoFocus
                            />
                        </div>
                        <p className="help-text">
                            A new RSA keypair will be generated. You will receive the private <code>.pem</code> file once, then use this SSH key when launching a virtual machine.
                        </p>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="cancel-button"
                                onClick={() => {
                                    setShowCreateKeypair(false);
                                    setNewKeypairName('');
                                    setCreateMode('upload');
                                }}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button disabled={loading} className="submit-button" type="submit">
                                {loading ? 'Generating...' : 'Generate Keypair'}
                            </button>
                        </div>
                    </form>
                )}
            </Modal>

            <Modal
                isOpen={generatedPrivateKey !== null}
                onClose={() => undefined}
                title="Your Private Key"
                size="lg"
                closeOnEscape={false}
                showCloseButton={false}
            >
                <div className="private-key-modal">
                    <div className="private-key-warning">
                        <strong>Save this key now.</strong> This is the only time the private key will be shown. It cannot be retrieved after you close this window.
                    </div>
                    <pre className="private-key-display">{generatedPrivateKey}</pre>
                    <div className="private-key-actions">
                        <button className="cancel-button" type="button" onClick={async () => {
                            if (await handleCopyPrivateKey()) setHasAcknowledgedPrivateKey(true);
                        }}>
                            <FaCopy /> Copy
                        </button>
                        <button className="submit-button" type="button" onClick={handleDownloadPrivateKey}>
                            <FaDownload /> Download {generatedKeypairName}.pem
                        </button>
                    </div>
                    <label className="secret-key-acknowledgement">
                        <input
                            type="checkbox"
                            checked={hasAcknowledgedPrivateKey}
                            onChange={(event) => setHasAcknowledgedPrivateKey(event.target.checked)}
                        />
                        <span>I have saved or deliberately discarded this key. I understand it cannot be retrieved later.</span>
                    </label>
                    <div className="modal-actions">
                        <button className="submit-button" type="button" disabled={!hasAcknowledgedPrivateKey} onClick={handleClosePrivateKeyModal}>
                            Done
                        </button>
                    </div>
                </div>
            </Modal>

            <DeleteKeypairModal
                isOpen={keypairToDelete !== null}
                keypair={keypairToDelete}
                onClose={() => setKeypairToDelete(null)}
                onConfirm={handleConfirmDelete}
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default KeyPairsPage;
